import { useState, useEffect, useCallback, useRef } from 'react';
import { socketService } from '../services/unifiedSocketService';
import { logger } from '../utils/logger';
import CryptoJS from 'crypto-js';
import authService from '../services/authService';

interface NetworkState {
  isOnline: boolean;
  isReconnecting: boolean;
  reconnectAttempts: number;
  lastDisconnect: Date | null;
  connectionQuality: 'excellent' | 'good' | 'poor' | 'offline';
  latency: number;
}

interface ConsultationState {
  sessionId: string;
  timestamp: Date;
  videoEnabled: boolean;
  audioEnabled: boolean;
  chatMessages: unknown[];
  notes: string;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY_BASE = 1000; // 1 seconde
const PING_INTERVAL = 5000; // 5 secondes

export const useNetworkResilience = (sessionId?: string) => {
  const [networkState, setNetworkState] = useState<NetworkState>({
    isOnline: navigator.onLine,
    isReconnecting: false,
    reconnectAttempts: 0,
    lastDisconnect: null,
    connectionQuality: 'excellent',
    latency: 0
  });

  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const savedStateRef = useRef<ConsultationState | null>(null);

  // Sauvegarder l'état de la consultation
  const saveConsultationState = useCallback(async (state: Partial<ConsultationState>) => {
    if (!sessionId) return;
    try {
      const currentState: ConsultationState = {
        sessionId,
        timestamp: new Date(),
        videoEnabled: state.videoEnabled ?? true,
        audioEnabled: state.audioEnabled ?? true,
        chatMessages: state.chatMessages ?? [],
        notes: state.notes ?? ''
      };

      const token = authService.getToken();
      if (token) {
        // Chiffrer l'état de la consultation avec AES avant stockage
        const dataToEncrypt = JSON.stringify(currentState);
        const encryptedState = CryptoJS.AES.encrypt(dataToEncrypt, token).toString();
        
        sessionStorage.setItem(
          `consultation_state_${sessionId}`,
          encryptedState
        );
      } else {
        // Fallback sans chiffrement si le token est manquant
        sessionStorage.setItem(
          `consultation_state_${sessionId}`,
          JSON.stringify(currentState)
        );
      }

      savedStateRef.current = currentState;

      logger.debug('[NetworkResilience] État consultation sauvegardé et chiffré');
    } catch (error) {
      logger.error('[NetworkResilience] Erreur sauvegarde état', error);
    }
  }, [sessionId]);

  // Restaurer l'état de la consultation
  const restoreConsultationState = useCallback(async (): Promise<ConsultationState | null> => {
    if (!sessionId) return null;
    try {
      const savedState = sessionStorage.getItem(`consultation_state_${sessionId}`);
      
      if (savedState) {
        const token = authService.getToken();
        if (token) {
          try {
            const bytes = CryptoJS.AES.decrypt(savedState, token);
            const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
            if (decryptedData) {
              const state: ConsultationState = JSON.parse(decryptedData);
              savedStateRef.current = state;
              logger.debug('[NetworkResilience] État consultation déchiffré et restauré', state);
              return state;
            }
          } catch (decryptError) {
            logger.error('[NetworkResilience] Échec déchiffrement AES, tentative parsing brut', decryptError);
          }
        }

        // Fallback si non chiffré ou si échec déchiffrement
        const state: ConsultationState = JSON.parse(savedState);
        savedStateRef.current = state;
        
        logger.debug('[NetworkResilience] État consultation restauré (fallback brut)', state);
        return state;
      }

      return null;
    } catch (error) {
      logger.error('[NetworkResilience] Erreur restauration état', error);
      return null;
    }
  }, [sessionId]);

  // Mesurer la latence réseau
  const measureLatency = useCallback(async (): Promise<number> => {
    const startTime = Date.now();
    
    try {
      // Ping le serveur via Socket.IO
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Timeout')), 5000);
        
        (socketService as unknown as { emit: (event: string, data: Record<string, unknown>, callback: () => void) => void }).emit('ping', {}, () => {
          clearTimeout(timeout);
          resolve();
        });
      });

      const latency = Date.now() - startTime;
      return latency;
    } catch (error) {
      logger.error('[NetworkResilience] Erreur mesure latence', error);
      return -1;
    }
  }, []);

  // Déterminer la qualité de connexion
  const determineConnectionQuality = useCallback((latency: number): NetworkState['connectionQuality'] => {
    if (latency < 0) return 'offline';
    if (latency < 100) return 'excellent';
    if (latency < 300) return 'good';
    return 'poor';
  }, []);

  // Tentative de reconnexion avec backoff exponentiel
  const attemptReconnect = useCallback(async (attempt: number) => {
    if (attempt >= MAX_RECONNECT_ATTEMPTS) {
      logger.warn('[NetworkResilience] Nombre maximum de tentatives atteint');
      setNetworkState(prev => ({
        ...prev,
        isReconnecting: false,
        connectionQuality: 'offline'
      }));
      return;
    }

    const delay = RECONNECT_DELAY_BASE * Math.pow(2, attempt);
    logger.info(`[NetworkResilience] Tentative ${attempt + 1}/${MAX_RECONNECT_ATTEMPTS} dans ${delay}ms`);

    reconnectTimeoutRef.current = setTimeout(async () => {
      try {
        // Vérifier la connexion
        const latency = await measureLatency();
        
        if (latency > 0) {
          // Connexion rétablie
          logger.info('[NetworkResilience] Connexion rétablie');
          
          // Restaurer l'état
          await restoreConsultationState();
          
          setNetworkState(prev => ({
            ...prev,
            isOnline: true,
            isReconnecting: false,
            reconnectAttempts: 0,
            connectionQuality: determineConnectionQuality(latency),
            latency
          }));

          // Notifier via Socket.IO
          if (sessionId) {
            socketService.emit('consultation:reconnected', {
              sessionId,
              timestamp: new Date()
            });
          }
        } else {
          // Nouvelle tentative
          setNetworkState(prev => ({
            ...prev,
            reconnectAttempts: attempt + 1
          }));
          
          attemptReconnect(attempt + 1);
        }
      } catch (error) {
        logger.error('[NetworkResilience] Erreur reconnexion', error);
        attemptReconnect(attempt + 1);
      }
    }, delay);
  }, [sessionId, measureLatency, determineConnectionQuality, restoreConsultationState]);

  // Gérer la perte de connexion
  const handleOffline = useCallback(async () => {
    logger.warn('[NetworkResilience] Connexion perdue');

    setNetworkState(prev => ({
      ...prev,
      isOnline: false,
      isReconnecting: true,
      reconnectAttempts: 0,
      lastDisconnect: new Date(),
      connectionQuality: 'offline'
    }));

    // Sauvegarder l'état actuel
    if (sessionId) {
      await saveConsultationState({
        sessionId,
        timestamp: new Date()
      });

      // Notifier via Socket.IO (si encore connecté)
      try {
        socketService.emit('consultation:disconnected', {
          sessionId,
          timestamp: new Date()
        });
      } catch {
        logger.error('[NetworkResilience] Impossible de notifier la déconnexion');
      }
    }

    // Démarrer les tentatives de reconnexion
    attemptReconnect(0);
  }, [sessionId, saveConsultationState, attemptReconnect]);

  // Gérer le retour de connexion
  const handleOnline = useCallback(async () => {
    logger.info('[NetworkResilience] Connexion détectée');

    // Annuler les tentatives de reconnexion en cours
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Mesurer la latence
    const latency = await measureLatency();

    setNetworkState(prev => ({
      ...prev,
      isOnline: true,
      isReconnecting: false,
      reconnectAttempts: 0,
      connectionQuality: determineConnectionQuality(latency),
      latency
    }));

    // Restaurer l'état
    await restoreConsultationState();

    // Notifier via Socket.IO
    if (sessionId) {
      socketService.emit('consultation:reconnected', {
        sessionId,
        timestamp: new Date()
      });
    }
  }, [sessionId, measureLatency, determineConnectionQuality, restoreConsultationState]);

  // Monitoring continu de la qualité réseau
  useEffect(() => {
    const monitorNetwork = async () => {
      if (!networkState.isOnline) return;

      const latency = await measureLatency();
      
      setNetworkState(prev => ({
        ...prev,
        latency,
        connectionQuality: determineConnectionQuality(latency)
      }));
    };

    // Ping toutes les 5 secondes
    pingIntervalRef.current = setInterval(monitorNetwork, PING_INTERVAL);

    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [networkState.isOnline, measureLatency, determineConnectionQuality]);

  // Écouter les événements réseau
  useEffect(() => {
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener('socket:reconnected', handleOnline);

    // Écouter les événements Socket.IO
    socketService.on('disconnect', handleOffline);
    socketService.on('connect', handleOnline);
    socketService.on('reconnect', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('socket:reconnected', handleOnline);
      
      socketService.off('disconnect', handleOffline);
      socketService.off('connect', handleOnline);
      socketService.off('reconnect', handleOnline);

      // Nettoyer les timeouts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [handleOffline, handleOnline]);

  // Forcer une reconnexion manuelle
  const forceReconnect = useCallback(async () => {
    logger.info('[NetworkResilience] Reconnexion forcée');
    
    setNetworkState(prev => ({
      ...prev,
      isReconnecting: true,
      reconnectAttempts: 0
    }));

    await attemptReconnect(0);
  }, [attemptReconnect]);

  return {
    ...networkState,
    saveConsultationState,
    restoreConsultationState,
    forceReconnect,
    measureLatency
  };
};
